import React, { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Eye, Download, DollarSign, Activity } from 'lucide-react';

export default function AnalyticsChart({
  title = "Performance Overview",
  dataSeries = null,
  color = "var(--primary)"
}) {
  const [activeRange, setActiveRange] = useState('30d');
  const [activeMetric, setActiveMetric] = useState('revenue');
  const [hoverIndex, setHoverIndex] = useState(null);

  // Generate realistic data points based on selected range and metric
  const chartData = useMemo(() => {
    if (dataSeries) return dataSeries;

    const count = activeRange === '7d' ? 7 : activeRange === '30d' ? 15 : activeRange === '90d' ? 18 : 12;
    const points = [];
    const now = new Date();

    for (let i = count - 1; i >= 0; i--) {
      const d = new Date();
      if (activeRange === '7d') d.setDate(now.getDate() - i);
      else if (activeRange === '30d') d.setDate(now.getDate() - i * 2);
      else if (activeRange === '90d') d.setDate(now.getDate() - i * 5);
      else d.setMonth(now.getMonth() - i);

      const label = activeRange === '7d' 
        ? d.toLocaleDateString('en-US', { weekday: 'short' })
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Base curves
      const baseRev = 45 + Math.sin(i * 0.8) * 20 + (count - i) * 6 + (Math.sin(i * 2.3) * 12);
      const baseDownloads = Math.floor(baseRev * 1.8 + Math.cos(i) * 10);
      const baseViews = baseDownloads * 9 + Math.floor(Math.sin(i) * 50);

      points.push({
        label,
        revenue: Math.max(12, Math.round(baseRev)),
        downloads: Math.max(5, baseDownloads),
        views: Math.max(30, baseViews),
      });
    }
    return points;
  }, [activeRange, dataSeries]);

  const values = chartData.map(d => d[activeMetric]);
  const maxValue = Math.max(...values, 10);
  const minValue = Math.min(...values);
  const total = values.reduce((a, b) => a + b, 0);
  const average = Math.round(total / values.length);
  const changePercent = '+18.4%';

  // SVG dimensions
  const svgWidth = 720;
  const svgHeight = 220;
  const paddingX = 30;
  const paddingY = 30;
  const graphWidth = svgWidth - paddingX * 2;
  const graphHeight = svgHeight - paddingY * 2;

  // Build SVG path points
  const points = chartData.map((d, idx) => {
    const x = paddingX + (idx / (chartData.length - 1)) * graphWidth;
    const normalizedY = (d[activeMetric] - Math.min(0, minValue)) / (maxValue - Math.min(0, minValue) || 1);
    const y = svgHeight - paddingY - normalizedY * graphHeight;
    return { x, y, val: d[activeMetric], label: d.label };
  });

  // Smooth bezier curve path
  const pathD = useMemo(() => {
    if (points.length === 0) return '';
    let d = `M ${points[0].x},${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const cpX = (p0.x + p1.x) / 2;
      d += ` C ${cpX},${p0.y} ${cpX},${p1.y} ${p1.x},${p1.y}`;
    }
    return d;
  }, [points]);

  const areaD = `${pathD} L ${points[points.length - 1]?.x || 0},${svgHeight - paddingY} L ${points[0]?.x || 0},${svgHeight - paddingY} Z`;

  const getMetricIcon = () => {
    if (activeMetric === 'revenue') return <DollarSign size={16} color="var(--primary)" />;
    if (activeMetric === 'downloads') return <Download size={16} color="var(--accent)" />;
    return <Eye size={16} color="var(--info)" />;
  };

  const formatMetricValue = (val) => {
    if (activeMetric === 'revenue') return `$${val.toLocaleString()}`;
    return val.toLocaleString();
  };

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '20px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header & Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Activity size={18} color="var(--primary)" />
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{title}</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
              {formatMetricValue(total)}
            </span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.8125rem',
              fontWeight: 700,
              color: 'var(--success)',
              background: 'var(--success-subtle)',
              padding: '2px 8px',
              borderRadius: '999px'
            }}>
              <TrendingUp size={12} /> {changePercent} vs last period
            </span>
          </div>
        </div>

        {/* Metric Switcher & Timeframe Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Metric Selector */}
          <div style={{
            display: 'flex',
            background: 'var(--bg-base)',
            padding: '3px',
            borderRadius: '10px',
            border: '1px solid var(--border-subtle)'
          }}>
            {[
              { id: 'revenue', label: 'Revenue', icon: DollarSign },
              { id: 'downloads', label: 'Downloads', icon: Download },
              { id: 'views', label: 'Views', icon: Eye }
            ].map(m => (
              <button
                key={m.id}
                onClick={() => setActiveMetric(m.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '7px',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  background: activeMetric === m.id ? 'var(--bg-surface)' : 'transparent',
                  color: activeMetric === m.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                  boxShadow: activeMetric === m.id ? 'var(--shadow-xs)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                <m.icon size={13} />
                {m.label}
              </button>
            ))}
          </div>

          {/* Timeframe Selector */}
          <div style={{
            display: 'flex',
            background: 'var(--bg-base)',
            padding: '3px',
            borderRadius: '10px',
            border: '1px solid var(--border-subtle)'
          }}>
            {['7d', '30d', '90d', '1y'].map(range => (
              <button
                key={range}
                onClick={() => setActiveRange(range)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '7px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: activeRange === range ? 'var(--primary)' : 'transparent',
                  color: activeRange === range ? '#FFFFFF' : 'var(--text-secondary)',
                  transition: 'all 0.15s ease'
                }}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Interactive SVG Canvas */}
      <div style={{ position: 'relative', width: '100%', height: '220px' }}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
          <defs>
            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.32" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.0" />
            </linearGradient>
            <linearGradient id="strokeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--primary)" />
              <stop offset="100%" stopColor="var(--accent)" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {[0, 0.33, 0.66, 1].map((ratio, i) => {
            const y = paddingY + ratio * graphHeight;
            return (
              <line
                key={i}
                x1={paddingX}
                y1={y}
                x2={svgWidth - paddingX}
                y2={y}
                stroke="var(--border-subtle)"
                strokeDasharray="4 4"
                strokeWidth="1"
              />
            );
          })}

          {/* Area Fill */}
          <path d={areaD} fill="url(#chartGradient)" />

          {/* Curve Stroke */}
          <path
            d={pathD}
            fill="none"
            stroke="url(#strokeGradient)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Interactive Data Dots & Hover Hitbox */}
          {points.map((p, idx) => (
            <g key={idx} onMouseEnter={() => setHoverIndex(idx)} onMouseLeave={() => setHoverIndex(null)}>
              {/* Invisible wide hit target for hover */}
              <circle cx={p.x} cy={p.y} r="18" fill="transparent" cursor="pointer" />
              
              {/* Visible dot */}
              <circle
                cx={p.x}
                cy={p.y}
                r={hoverIndex === idx ? 6 : 3.5}
                fill={hoverIndex === idx ? '#FFFFFF' : 'var(--primary)'}
                stroke={hoverIndex === idx ? 'var(--primary)' : 'var(--bg-surface)'}
                strokeWidth={hoverIndex === idx ? 3 : 2}
                style={{ transition: 'all 0.15s ease' }}
              />

              {/* X Axis Labels */}
              {(idx === 0 || idx === Math.floor(points.length / 2) || idx === points.length - 1 || hoverIndex === idx) && (
                <text
                  x={p.x}
                  y={svgHeight - 8}
                  textAnchor="middle"
                  fill="var(--text-tertiary)"
                  fontSize="11"
                  fontWeight="600"
                >
                  {p.label}
                </text>
              )}
            </g>
          ))}
        </svg>

        {/* Hover Tooltip Overlay */}
        {hoverIndex !== null && points[hoverIndex] && (
          <div
            style={{
              position: 'absolute',
              top: Math.max(10, (points[hoverIndex].y / svgHeight) * 100 - 24) + '%',
              left: `calc(${(points[hoverIndex].x / svgWidth) * 100}% - 50px)`,
              transform: 'translateY(-100%)',
              background: 'var(--bg-surface-active)',
              border: '1px solid var(--border-medium)',
              borderRadius: '10px',
              padding: '6px 12px',
              boxShadow: 'var(--shadow-md)',
              pointerEvents: 'none',
              zIndex: 10,
              textAlign: 'center',
              animation: 'fadeIn 0.15s ease'
            }}
          >
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
              {points[hoverIndex].label}
            </div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {getMetricIcon()}
              {formatMetricValue(points[hoverIndex].val)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
